import { cn } from "@heroui/react";
import { m } from "framer-motion";
import DemoAvatar from "./DemoAvatar";
import { resolveConversationPhoto } from "../hooks/demoUtils";
import {
  messageEnterAnimate,
  messageEnterInitial,
  messageEnterTransition,
} from "../hooks/messageMotion";
import type { Conversation, InboxAvatarTone, Message } from "../data/types";

type MessageBubbleProps = {
  message: Message;
  conversation?: Conversation;
  operatorAvatar?: string;
  operatorName?: string;
};

const AGENT_AVATAR = "/images/inbox-demo/3chat-logo.svg";

const senderTone: Record<Message["sender"], InboxAvatarTone> = {
  customer: "default",
  ai: "primary",
  human: "primary",
};

export default function MessageBubble({
  message,
  conversation,
  operatorAvatar,
  operatorName,
}: MessageBubbleProps) {
  const alignRight =
    message.align === "right" ||
    (message.align !== "left" && message.sender !== "customer");
  const isHuman = message.sender === "human";
  const isAi = message.sender === "ai";
  const isCustomer = message.sender === "customer";
  const customerPhoto = conversation
    ? resolveConversationPhoto(conversation)
    : undefined;
  const avatarSrc =
    message.avatar ||
    (isAi
      ? AGENT_AVATAR
      : isCustomer
        ? customerPhoto
        : operatorAvatar);

  return (
    <m.div
      animate={messageEnterAnimate}
      className={cn(
        "flex w-full items-start gap-2",
        alignRight ? "justify-end pl-10" : "justify-start pr-10",
      )}
      initial={messageEnterInitial}
      transition={messageEnterTransition}
    >
      {alignRight ? null : (
        <DemoAvatar
          initial={isCustomer ? conversation?.avatarInitial : undefined}
          name={
            isCustomer
              ? conversation?.name || message.senderName || "C"
              : message.senderName || "C"
          }
          size="sm"
          src={avatarSrc}
          tone={
            isCustomer
              ? conversation?.avatarTone ?? senderTone.customer
              : senderTone[message.sender]
          }
        />
      )}
      <div
        className={cn(
          "max-w-[min(100%,34rem)] whitespace-pre-wrap p-3 text-sm leading-5",
          alignRight
            ? "rounded-tl-2xl rounded-bl-2xl rounded-br-2xl"
            : "rounded-tr-2xl rounded-bl-2xl rounded-br-2xl",
          isHuman && "inbox-demo-human-bubble text-default-900",
          isAi && "inbox-demo-ai-bubble text-default-900",
          !isHuman && !isAi && "inbox-demo-customer-bubble text-default-900",
        )}
      >
        {message.content}
      </div>
      {alignRight ? (
        <DemoAvatar
          name={
            isAi
              ? message.senderName || "AI"
              : operatorName || message.senderName || "Me"
          }
          size="sm"
          src={avatarSrc}
          tone={isAi ? "primary" : "default"}
        />
      ) : null}
    </m.div>
  );
}
